<template>
  <div>
    <Loading v-show="loading" />
    <section v-if="!loading" class="p-box">
      <div class="p-box__body">
        <header class="header">
          {{ $t('pages.CompaniesInitialSettingGuide.header') }}
        </header>
        <div v-if="incompleteQuestionCards.length > 0" class="p-box">
          <div class="p-box__body">
            <div class="c-info c-info--primary suggest">
              <div class="c-info__icon"><Icon name="iconExclamation" /></div>
              <div class="c-info__content">
                <p>
                  {{ $t('pages.CompaniesInitialSettingGuide.settingSuggest', { number: incompleteQuestionCards.length }) }}
                </p>
              </div>
            </div>
            <div class="links">
              <div v-for="(question,questionIndex) in useQuestions" :key="'question_' + questionIndex">
                <template v-for="(card,cardIndex) in question.cards">
                  <div v-if="!completedItems[card.completedItemKey]" :key="'card_' + cardIndex + 1" class="c-links__item">
                    <a @click="scrollToAnchorPoint(`question${ question.localeIndex }`)">{{ $t(`pages.CompaniesInitialSettingGuide.question${ question.localeIndex }.card${cardIndex + 1}.tableOfContents`) }}</a>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Box v-for="(question,questionIndex) in useQuestions" :key="'question_' + questionIndex">
        <template #header>
          <span :ref="`question${ question.localeIndex }`" class="questionNumber">Q{{ questionIndex + 1 }}.</span>
          {{ $t(`pages.CompaniesInitialSettingGuide.question${ question.localeIndex }.header`) }}
        </template>
        <template #body>
          <div class="questionBody">
            <div class="questionBody__topSection">
              <div class="questionImg">
                <img :src="question.imgPath" alt="">
              </div>
              <div class="questionDescription">
                {{ $t(`pages.CompaniesInitialSettingGuide.question${ question.localeIndex }.description`) }}
              </div>
            </div>
            <div class="questionBody__bottomSection">
              <!-- 基本View -->
              <div v-for="(card,cardIndex) in question.cards" :key="'card_' + cardIndex + 1" class="questionCard">
                <div class="questionCard__header">
                  {{ $t(`pages.CompaniesInitialSettingGuide.question${ question.localeIndex }.card${cardIndex + 1}.header`) }}
                </div>
                <div class="questionCard__body">
                  <p v-for="textIndex in card.bodyTextAmounts" :key="'bodyText_' + textIndex">
                    <span class="questionCard__body__number">{{ textIndex }}.</span>
                    {{ $t(`pages.CompaniesInitialSettingGuide.question${ question.localeIndex }.card${cardIndex + 1}.body.text${textIndex}`) }}
                  </p>
                  <p v-if="card.withAnnotation">
                    ※ <Icon v-if="card.icon" name="iconProtection" />{{ $t(`pages.CompaniesInitialSettingGuide.question${ question.localeIndex }.card${cardIndex + 1}.body.annotation`) }}
                  </p>
                </div>
                <div class="c-button c-button--right">
                  <div class="questionBody__bottomSection__action">
                    <a :href="card.viewMoreLink" target="_blank" class="viewMoreLink">
                      {{ $t(`pages.CompaniesInitialSettingGuide.viewMore`) }}
                    </a>
                  </div>
                  <div v-if="!completedItems[card.completedItemKey]" class="questionBody__bottomSection__action">
                    <KkButton
                      color="secondary"
                      outlined
                      :font-bold="false"
                      :text="$t('pages.CompaniesInitialSettingGuide.notSetup')"
                      @click="completeSetup(card.completedItemKey)"
                    />
                  </div>
                  <div class="questionBody__bottomSection__action">
                    <KkButton
                      color="primary"
                      :to="card.settingRouteObj"
                      :disabled="completedItems[card.completedItemKey]"
                      :text="completedItems[card.completedItemKey] ? $t('pages.CompaniesInitialSettingGuide.completed') : $t(`pages.CompaniesInitialSettingGuide.question${questionIndex + 1}.card${cardIndex + 1}.goSetting`)"
                    />
                  </div>
                </div>
              </div>
              <!-- end 基本View -->
            </div>
          </div>
        </template>
      </Box>
    </section>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { KkButton } from '../../components/base'
import Icon from '../../components/Icon'
import Box from '../../components/Box'
import RegularHourImg from '../../../../images/initial_setting_guides/RegularHourImg.jpg'
import ConnectionSiteImg from '../../../../images/initial_setting_guides/ConnectionSiteImg.jpg'
import PageSite from '../../../../images/initial_setting_guides/PageSite.jpg'
import KeywordAnalysis from '../../../../images/initial_setting_guides/KeywordAnalysis.jpg'
import MemberInvite from '../../../../images/initial_setting_guides/MemberInvite.jpg'
import Loading from '../../components/Loading'

export default {
  name: 'CompaniesInitialSettingGuide',
  components: { Loading, Box, Icon, KkButton },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.readInitialSettingGuideConfirmations()
    })
  },
  computed: {
    ...mapState({
      stores: state => state.companiesStores.data?.data,
      completedItems: state => state.companiesInitialSetupGuides.data,
      loading: state => state.companiesInitialSetupGuides.loading || state.companiesStores.loading
    }),
    ...mapGetters({
      providedFeaturesAnalysisKeyword: 'companiesCompany/providedFeaturesAnalysisKeyword',
      providedFeaturesVisitor: 'companiesCompany/providedFeaturesVisitor',
    }),
    RegularHourImgPath: () => RegularHourImg,
    ConnectionSiteImgPath: () => ConnectionSiteImg,
    PageSitePath: () => PageSite,
    KeywordAnalysisPath: () => KeywordAnalysis,
    MemberInvitePath: () => MemberInvite,
    firstStoreUuid() {
      return this.stores[0].attributes.uuid
    },
    companyUuid() {
      return this.$route.params.company_uuid
    },
    useQuestions() {
      return this.questions.filter(question => question.display)
    },
    incompleteQuestionCards() {
      const cards = this.useQuestions.flatMap(question => question.cards)
      return cards.filter(card => !this.completedItems[card.completedItemKey])
    },
    /**
     *
     * @return {[{cards: [{completedItemKey: string, viewMoreLink: string, ?icon: boolean, withAnnotation: boolean, bodyTextAmounts: number, settingRouteObj: {name: string, params: {company_uuid: default.computed.$route.params.company_uuid, store_uuid}}}], imgPath}]}
     */
    questions() {
      return [
        {
          imgPath: this.RegularHourImgPath,
          display: true,
          localeIndex: 1,
          cards: [
            {
              bodyTextAmounts: 2,
              withAnnotation: true,
              viewMoreLink: 'https://movinc.notion.site/df82edb4477f46088d21139f9166a7f0',
              settingRouteObj: { name: 'CompaniesLocationStore', params: { company_uuid: this.companyUuid, store_uuid: this.firstStoreUuid }},
              completedItemKey: 'storeGmbSyncEnabled',
              icon: true,
            },
          ],
        },
        {
          imgPath: this.ConnectionSiteImgPath,
          display: true,
          localeIndex: 2,
          cards: [
            {
              bodyTextAmounts: 2,
              withAnnotation: true,
              viewMoreLink: 'https://movinc.notion.site/Google-3ae40f2bcf954c948abca28a4ceb65fd',
              settingRouteObj: { name: 'CompaniesLocationStore', params: { company_uuid: this.companyUuid, store_uuid: this.firstStoreUuid }, query: { tab: 'connection' }},
              completedItemKey: 'storeSiteRegistered',
            },
            // {
            //   bodyTextAmounts: 2,
            //   withAnnotation: true,
            //   viewMoreLink: 'https://www.notion.so/movinc/ID-f060b672314a415f9b8726e0d985efbd',
            //   settingRouteObj: { name: 'CompaniesLocationStore', params: { company_uuid: this.companyUuid, store_uuid: this.firstStoreUuid }, query: { tab: 'connection' }},
            //   completedItemKey: 'storeExternalAccountRegistered',
            // },
          ],
        },
        {
          imgPath: this.PageSitePath,
          display: this.providedFeaturesVisitor,
          localeIndex: 3,
          cards: [
            {
              bodyTextAmounts: 2,
              withAnnotation: false,
              viewMoreLink: 'https://movinc.notion.site/6e46461fd93a410db11316a5cc751c76',
              settingRouteObj: { name: 'CompaniesVisitorStoreEdit', params: { company_uuid: this.companyUuid, store_uuid: this.firstStoreUuid }},
              completedItemKey: 'storeVisitorPageConfirmed',
            },
          ],
        },
        {
          imgPath: this.KeywordAnalysisPath,
          display: this.providedFeaturesAnalysisKeyword,
          localeIndex: 4,
          cards: [
            {
              bodyTextAmounts: 2,
              withAnnotation: false,
              viewMoreLink: 'https://movinc.notion.site/27df725c0c654e1da351e64117a7e247',
              settingRouteObj: { name: 'CompaniesAnalysisKeywords', params: { company_uuid: this.companyUuid }},
              completedItemKey: 'analysisReviewKeywordRegistered',
            },
          ],
        },
        {
          imgPath: this.MemberInvitePath,
          display: true,
          localeIndex: 5,
          cards: [
            {
              bodyTextAmounts: 2,
              withAnnotation: false,
              viewMoreLink: 'https://movinc.notion.site/3e6f1b1c54384aaeacfe2de3c67c0b35',
              settingRouteObj: { name: 'CompaniesManagementMembers', params: { company_uuid: this.companyUuid }},
              completedItemKey: 'userInvited',
            },
          ],
        },
      ]
    },
  },
  created() {
    this.readInitialSettingGuideConfirmations()
  },
  methods: {
    ...mapActions({
      readInitialSettingGuideConfirmations: 'companiesInitialSetupGuides/readInitialSettingGuideConfirmations',
      completeSetupGuide: 'companiesInitialSetupGuides/completeSetupGuide'
    }),
    scrollToAnchorPoint(refName) {
      const el = this.$refs[refName][0]
      el.scrollIntoView({ behavior: 'smooth' })
    },
    completeSetup(guideKey) {
      if (!confirm(this.$t('pages.CompaniesInitialSettingGuide.confirmToCompleteSetup'))) return

      this.completeSetupGuide(guideKey)

    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../stylesheets/biz_spa/foundation/_var.scss';

.header {
  font-size: 24px;
  margin: 16px 0 32px 0;
}
.suggest {
  width: auto !important;
}
.links {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0 -16px -16px;
}
.questionNumber {
  color: $color-accent-primary;
}
.questionBody {
  &__topSection {
    display: flex;
    margin-bottom: 24px;
  }

  &__bottomSection {
    &__action {
      &:not(:first-child) {
        margin-left: 16px;
      }
    }
  }
}
.questionImg {
  max-width: 320px;
}
.questionDescription {
  font-size: 16px;
  margin: auto auto auto 40px;
}
.questionCard {
  margin: 24px 0;
  padding: 32px;
  border-radius: 4px;
  box-shadow: 0 0 0 rgba(63, 63, 68, .05);
  border: 1px solid $color-base-c;
  &__header {
    margin-bottom: 32px;
    font-size: 16px;
    font-weight: bold;
  }
  &__body p {
    margin: 16px 0;
  }
  &__body__number {
    font-weight: bold;
  }
}
.viewMoreLink {
  color: $color-accent-primary;
  text-decoration: none;
}
</style>
